<script setup lang="ts">
import { computed, ref, watch } from "vue"

interface CookieDescriptor {
    name: string
    provider: string
    purpose_key: string
    duration_key: string
    type: string
}

interface CategoryDescriptor {
    key: "necessary" | "functional" | "analytics" | "marketing"
    required: boolean
    cookies: CookieDescriptor[]
}

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ "update:modelValue": [value: boolean] }>()

const open = computed({
    get: () => props.modelValue,
    set: (v) => emit("update:modelValue", v),
})

const { choices, acceptAll, rejectAll, save } = useConsent()

const local = ref({
    functional: choices.value.functional,
    analytics: choices.value.analytics,
    marketing: choices.value.marketing,
})

watch(open, (isOpen) => {
    if (isOpen) {
        local.value = {
            functional: choices.value.functional,
            analytics: choices.value.analytics,
            marketing: choices.value.marketing,
        }
    }
})

const expanded = ref<Record<string, boolean>>({})
const toggleExpand = (key: string) => {
    expanded.value[key] = !expanded.value[key]
}

const categories: CategoryDescriptor[] = [
    {
        key: "necessary",
        required: true,
        cookies: [
            { name: "dapamas_consent", provider: "Dapamas", purpose_key: "consent.cookies.dapamas_consent.purpose", duration_key: "consent.duration.year_1", type: "HTTP" },
            { name: "XSRF-TOKEN", provider: "Dapamas", purpose_key: "consent.cookies.xsrf.purpose", duration_key: "consent.duration.session", type: "HTTP" },
            { name: "dapamas_session", provider: "Dapamas", purpose_key: "consent.cookies.session.purpose", duration_key: "consent.duration.session", type: "HTTP" },
            { name: "selected-province", provider: "Dapamas", purpose_key: "consent.cookies.province.purpose", duration_key: "consent.duration.days_30", type: "HTTP" },
        ],
    },
    {
        key: "functional",
        required: false,
        cookies: [
            { name: "shops", provider: "Dapamas", purpose_key: "consent.cookies.shops.purpose", duration_key: "consent.duration.persistent", type: "localStorage" },
        ],
    },
    {
        key: "analytics",
        required: false,
        cookies: [
            { name: "_ga", provider: "Google LLC", purpose_key: "consent.cookies.ga.purpose", duration_key: "consent.duration.years_2", type: "HTTP" },
            { name: "_ga_*", provider: "Google LLC", purpose_key: "consent.cookies.ga_session.purpose", duration_key: "consent.duration.years_2", type: "HTTP" },
            { name: "_gid", provider: "Google LLC", purpose_key: "consent.cookies.gid.purpose", duration_key: "consent.duration.hours_24", type: "HTTP" },
        ],
    },
    {
        key: "marketing",
        required: false,
        cookies: [
            { name: "_gcl_au", provider: "Google LLC", purpose_key: "consent.cookies.gcl.purpose", duration_key: "consent.duration.months_3", type: "HTTP" },
            { name: "IDE", provider: "Google LLC (DoubleClick)", purpose_key: "consent.cookies.ide.purpose", duration_key: "consent.duration.year_1", type: "HTTP" },
        ],
    },
]

const onAcceptAll = () => {
    acceptAll()
    open.value = false
}
const onRejectAll = () => {
    rejectAll()
    open.value = false
}
const onSave = () => {
    save({
        functional: local.value.functional,
        analytics: local.value.analytics,
        marketing: local.value.marketing,
    })
    open.value = false
}
</script>

<template>
    <q-dialog
        v-model="open"
        persistent
        :maximized="$q.screen.lt.sm"
        transition-show="slide-up"
        transition-hide="slide-down"
    >
        <q-card class="consent-modal">
            <q-card-section class="tw-flex tw-items-center tw-justify-between tw-border-b">
                <div class="tw-flex tw-items-center tw-gap-2">
                    <q-icon name="cookie" size="md" color="primary" />
                    <div class="text-h6">{{ $t("consent.modal.title") }}</div>
                </div>
                <q-btn
                    flat
                    round
                    dense
                    icon="close"
                    :aria-label="$t('actions.close')"
                    @click="open = false"
                />
            </q-card-section>

            <q-card-section class="tw-text-sm tw-text-gray-700 tw-leading-relaxed">
                {{ $t("consent.modal.intro") }}
                <NuxtLink
                    to="/information/cookie-policy"
                    class="tw-text-primary tw-underline"
                >
                    {{ $t("consent.modal.policy_link") }}
                </NuxtLink>
            </q-card-section>

            <q-card-section class="tw-px-4 tw-pt-0 tw-pb-2 tw-space-y-3">
                <div
                    v-for="cat in categories"
                    :key="cat.key"
                    class="consent-category"
                >
                    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <div class="tw-flex-1">
                            <button
                                type="button"
                                class="consent-category-header"
                                :aria-expanded="!!expanded[cat.key]"
                                @click="toggleExpand(cat.key)"
                            >
                                <q-icon
                                    :name="expanded[cat.key] ? 'expand_less' : 'expand_more'"
                                    size="sm"
                                />
                                <span class="tw-font-semibold tw-text-base">
                                    {{ $t(`consent.categories.${cat.key}.label`) }}
                                </span>
                            </button>
                            <p class="tw-text-sm tw-text-gray-600 tw-mt-1 tw-mb-0">
                                {{ $t(`consent.categories.${cat.key}.description`) }}
                            </p>
                        </div>
                        <q-toggle
                            v-if="!cat.required"
                            v-model="local[cat.key as 'functional' | 'analytics' | 'marketing']"
                            color="primary"
                            :aria-label="$t(`consent.categories.${cat.key}.label`)"
                            :data-cy="`consent-toggle-${cat.key}`"
                        />
                        <q-toggle
                            v-else
                            :model-value="true"
                            disable
                            color="primary"
                            :aria-label="$t(`consent.categories.${cat.key}.label`)"
                        />
                    </div>

                    <div v-if="expanded[cat.key]" class="tw-mt-3 tw-overflow-x-auto">
                        <table class="consent-table">
                            <thead>
                                <tr>
                                    <th>{{ $t("consent.table.name") }}</th>
                                    <th>{{ $t("consent.table.provider") }}</th>
                                    <th>{{ $t("consent.table.purpose") }}</th>
                                    <th>{{ $t("consent.table.duration") }}</th>
                                    <th>{{ $t("consent.table.type") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ck in cat.cookies" :key="ck.name">
                                    <td class="tw-font-mono tw-text-xs">{{ ck.name }}</td>
                                    <td>{{ ck.provider }}</td>
                                    <td>{{ $t(ck.purpose_key) }}</td>
                                    <td>{{ $t(ck.duration_key) }}</td>
                                    <td>{{ ck.type }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </q-card-section>

            <q-card-actions
                class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 tw-px-4 tw-py-4 tw-border-t"
            >
                <q-btn
                    no-caps
                    unelevated
                    color="grey-3"
                    text-color="primary"
                    class="consent-modal-btn"
                    :label="$t('consent.banner.reject_all')"
                    data-cy="consent-modal-reject-all"
                    @click="onRejectAll"
                />
                <q-btn
                    no-caps
                    unelevated
                    color="primary"
                    text-color="white"
                    class="consent-modal-btn"
                    :label="$t('consent.modal.save')"
                    data-cy="consent-modal-save"
                    @click="onSave"
                />
                <q-btn
                    no-caps
                    unelevated
                    color="secondary"
                    text-color="white"
                    class="consent-modal-btn"
                    :label="$t('consent.banner.accept_all')"
                    data-cy="consent-modal-accept-all"
                    @click="onAcceptAll"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<style scoped>
.consent-modal {
    width: 100%;
    max-width: 720px;
}

.consent-category {
    padding: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #fafafa;
}

.consent-category-header {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: inherit;
}

.consent-table {
    width: 100%;
    font-size: 12px;
    border-collapse: collapse;
}

.consent-table th,
.consent-table td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
}

.consent-table th {
    background-color: #f3f4f6;
    font-weight: 600;
}

.consent-modal-btn {
    flex: 1;
    min-height: 40px;
    font-weight: 600;
}
</style>
